/**
 * **플레이스 이미지가 통과해야 하는 두 축 한 곳** — 의존성이 없는 순수 모듈이다.
 *
 * 서버 `PlaceImageRules` 는 라벨(`image/png`·`image/jpeg`)과 **디코드한 바이트의 매직 넘버**가 모두
 * 맞아야, 그리고 원본이 2 MB 이하여야 통과시킨다. 화면은 `accept="image/*"` 로 GIF·WebP 를 고르게
 * 열어 두었고 크기도 5 MB 로 안내해서, 사람은 미리보기까지 본 뒤 저장에서 400 을 만났다. 서버가
 * 정본이고 화면은 **같은 값을 먼저** 말한다.
 *
 * 화면의 이 검사는 서버를 대신하지 않는다 — 드래그·확장자 위조는 여전히 서버가 마지막 방어선이다
 * (라벨은 주장이고 바이트가 사실이다).
 */

/** `<el-upload accept>` 문자열 — 파일 선택 대화상자가 거르는 축 */
export const PLACE_IMAGE_ACCEPT = 'image/png,image/jpeg'

/** 허용 MIME — 서버 `PlaceImageRules.ALLOWED_MIME` 과 같은 집합 */
export const PLACE_IMAGE_MIME = Object.freeze(['image/png', 'image/jpeg'])

/** 원본 크기 상한(MB) — 서버 `PlaceImageRules.MAX_DECODED_BYTES` 와 같은 값. 안내 문구도 이 상수에서 만든다 */
export const PLACE_IMAGE_MAX_MB = 2

/**
 * 바이트로 센 같은 상한 — 서버는 `2 * 1024 * 1024` 를 **초과**할 때 거절한다.
 *
 * 고른 파일의 `size` 는 디코드한 원본 바이트이고 서버가 재는 것도 그것이다(base64 문자열 길이가
 * 아니다 — 그렇게 재면 2 MB 이미지가 2.7 MB 로 보인다). 그래서 두 판정의 경계가 정확히 같다.
 */
export const PLACE_IMAGE_MAX_BYTES = PLACE_IMAGE_MAX_MB * 1024 * 1024

export const PLACE_IMAGE_TEXT = Object.freeze({
  mimeRejected: 'PNG 또는 JPEG 이미지만 등록할 수 있습니다.',
  maxSize: `최대 ${PLACE_IMAGE_MAX_MB}MB`,
  sizeRejected: `이미지는 원본 ${PLACE_IMAGE_MAX_MB}MB 이하여야 합니다.`,
})

/**
 * 고른 파일을 읽어도 되는가 — **읽기 전에** 묻는다(안 되면 미리보기도 만들지 않는다).
 *
 * 브라우저가 MIME 을 모르는 파일(`type` 이 빈 문자열)은 거절한다: 우리가 아는 것이 없으면 서버가
 * 거절할 쪽에 가깝고, 사람에게는 여기서 말하는 편이 저장 뒤의 400 보다 낫다.
 *
 * @param {?{type: string}} file `el-upload` 의 `file.raw`
 */
export function isAllowedPlaceImage(file) {
  const type = file && typeof file.type === 'string' ? file.type.trim().toLowerCase() : ''

  return PLACE_IMAGE_MIME.indexOf(type) > -1
}

/** 상한을 넘는가 — 크기를 모르는 파일은 넘지 않은 것으로 본다(서버가 답한다) */
export function exceedsPlaceImageSize(file) {
  const size = file && typeof file.size === 'number' ? file.size : 0

  return size > PLACE_IMAGE_MAX_BYTES
}

/**
 * 읽기 전 판정의 **단 하나의 입구** — 통과하면 `null`, 아니면 사람에게 할 말.
 *
 * 형식과 크기를 호출부마다 따로 물으면 두 이미지 입력의 답이 갈린다. 크기를 여기서 보는 것은
 * 치수·비율 검사가 파일을 **디코드한 뒤에야** 답할 수 있기 때문이다 — 2 MB 를 넘는 파일은 그 전에
 * 걸러야 한다.
 */
export function placeImageRejection(file) {
  if (!isAllowedPlaceImage(file)) return PLACE_IMAGE_TEXT.mimeRejected
  if (exceedsPlaceImageSize(file)) return PLACE_IMAGE_TEXT.sizeRejected

  return null
}
